import { RedoOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { CiLocationOn } from 'react-icons/ci';
import { MapContainer, Marker } from 'react-leaflet';

import icon from '../../../../../components/map/constants';
import { BaseLayers, MapCenterView, MapClickHandler } from '../../../../../components/map/mapParts';
import '../../../../../components/map/style.less';
import { fetchAddressUnit } from '../../../../../utils/addressHelper';

const { Text } = Typography;

const ADDRESS_URL = `${process.env.REACT_APP_ADDRESS_URL_V2}`;
const DEFAULT_CENTER = { lat: 47.9123565, lon: 106.9151432 };
const DEFAULT_REGION_ID = '11';

/** Газрын зураг болон хайлтын хэсгийн өндөр */
const PANEL_HEIGHT = 320;

/** Формын бусад талбартай нэг ирмэгт эгнэхийн тулд (Order.js-ийн FORM_MAX_WIDTH) */
const MAX_WIDTH = 960;

/**
 * Хүргэлтийн хаягийг газрын зургаас сонгох блок. Харагдац нь хүргэлтийн
 * захиалгын `components/map/AddressMap.js`-тэй ижил (`pp_map_panel`).
 *
 * СОНГОЛТЫГ ӨӨРТӨӨ ХАДГАЛАХГҮЙ: алхам солиход энэ компонент unmount болдог тул
 * дотоод state нь алга болно. Сонгосон хаягийг эцэг (Order.js) нь захиалгын
 * дугаараар store-д хадгалж, `selectedAddress`-аар буцааж өгнө.
 */
const AddressMap = ({ setAddress, selectedAddress = null, type }) => {
  const [addresses, setAddresses] = useState([]);
  const [aimagList, setAimagList] = useState([]);
  /** Хайлтын хүрээ (аймаг/хот) — хаягийн талбар биш, зөвхөн хайлтад хэрэглэнэ */
  const [regionId, setRegionId] = useState(DEFAULT_REGION_ID);
  const [regionName, setRegionName] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [searching, setSearching] = useState(false);
  const [regionsLoading, setRegionsLoading] = useState(true);
  // Сонгосон хаяг биш, зөвхөн зургийн харагдац — дарсан цэг рүү зөөнө
  const [center, setCenter] = useState(selectedAddress?.location ?? DEFAULT_CENTER);
  // Leaflet-д контейнер хэмжээтэй болсны дараа mount хийнэ (эс бөгөөс сааралдана)
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
    /**
     * Эхлэхэд орчмын хаягуудыг харуулна — жагсаалт хоосон нээгдэхгүй. Өмнө
     * сонгосон хаяг байвал түүний орчмоор хайна: жагсаалтад нь тэр хаяг орж
     * ирснээр сонголт нь тэмдэглэгдсэн хэвээр харагдана.
     */
    getNearbyAddresses(selectedAddress?.location ?? DEFAULT_CENTER);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchAddressUnit()
      .then(({ docs }) => {
        const aimags = docs.map((d) => ({
          label: d?.name ?? d?._source?.name_full_mn ?? d?._source?.name_mn,
          value: d.name,
          id: d._id,
        }));
        setAimagList(aimags);

        const defaultAimag = aimags.find((item) => item.id === DEFAULT_REGION_ID);
        if (defaultAimag) {
          setRegionName(defaultAimag.value);
          setRegionId(defaultAimag.id);
        }
      })
      .finally(() => setRegionsLoading(false));
  }, []);

  const getNearbyAddresses = ({ lat, lon }) => {
    setSearching(true);
    axios
      .get(`${ADDRESS_URL}/search-nearby-address`, {
        params: { lat, lon },
        headers: { 'X-Caller-Type': 'internal' },
      })
      .then(({ data = {} }) => {
        setAddresses(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('getNearbyAddresses error: ', err);
      })
      .finally(() => setSearching(false));
  };

  const getNearbyAddressesBySearch = () => {
    setSearching(true);
    axios
      .get(`${ADDRESS_URL}/search-nearby-address`, {
        params: { searchAddressText: searchKey, regionId },
        headers: { 'X-Caller-Type': 'internal' },
      })
      .then(({ data = {} }) => {
        setAddresses(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('getNearbyAddressesBySearch error: ', err);
      })
      .finally(() => setSearching(false));
  };

  const onSearch = () => {
    if (!regionId) {
      message.error('Хот/Аймаг сонгоно уу.');
      return;
    }
    if (!searchKey) {
      message.error('Хаяг оруулна уу.');
      return;
    }
    getNearbyAddressesBySearch();
  };

  /**
   * Газрын зураг дээр дарсан цэг — зөвхөн ойролцоох хаягуудыг гаргана.
   * Сонголтыг ХӨНДӨХГҮЙ: дарах нь хаяг сонгосон гэсэн үг биш (хэрэглэгч
   * жагсаалтаас батлах ёстой), тиймээс өмнөх сонголт нь хадгалагдана.
   */
  const onMapClick = ({ lat, lng }) => {
    setCenter({ lat, lon: lng });
    getNearbyAddresses({ lat, lon: lng });
  };

  /** Жагсаалтаас хаяг сонгох — id-аар хайж бүтэн объектыг эцэг рүү дамжуулна */
  const onSelectAddress = (addressId) => {
    const address = addresses.find((item) => item.addressId === addressId);
    if (!address) return;

    /**
     * Хайлтын зарим үр дүнд координат байдаггүй тул зургийн төвийг (хэрэглэгчийн
     * дарсан/хайсан цэг) нөөцөөр дамжуулна — эс бөгөөс захиалга байрлалгүй үүснэ.
     */
    setAddress?.({ ...address, location: address.location ?? center });
    if (address?.location) setCenter(address.location);
  };

  const resetView = () => setCenter(selectedAddress?.location ?? DEFAULT_CENTER);

  return (
    <div className="pp_map_panel" style={{ maxWidth: MAX_WIDTH }}>
      <Text style={{ display: 'block', marginBottom: 8 }}>
        <Text type="danger">*</Text>{' '}
        {type === 'direct-delivery' ? 'Хүргэлтийн хаягаа сонгоно уу' : 'Гэрийн хаягаа сонгоно уу'}
      </Text>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <div className="pp_map_panel__canvas" style={{ height: PANEL_HEIGHT }}>
            {ready && (
              <MapContainer
                center={[center.lat, center.lon]}
                zoom={16}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <BaseLayers />
                <MapClickHandler onClick={onMapClick} />
                <Marker position={[center.lat, center.lon]} icon={icon} />
                <MapCenterView center={center} />
              </MapContainer>
            )}

            <Tooltip title="Сонгосон хаяг руу буцах" placement="left">
              <Button
                className="pp_map_panel__reset"
                icon={<RedoOutlined rotate={270} />}
                onClick={resetView}
              />
            </Tooltip>
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {/* Хайлтын хүрээ — хаягийн талбар биш тул Form.Item-гүй */}
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Хайх хот/аймаг
              </Text>
              <Select
                placeholder="Сонгох"
                options={aimagList}
                value={regionName || undefined}
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                loading={regionsLoading}
                onChange={(value, option) => {
                  setRegionName(value);
                  setRegionId(option.id);
                }}
              />
            </div>

            <Input.Search
              placeholder="Байр, байгууламжийн нэрээр хайх"
              enterButton="Хайх"
              allowClear
              loading={searching}
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              onSearch={onSearch}
            />

            <div className="pp_map_panel__results" style={{ height: PANEL_HEIGHT - 104 }}>
              <Spin spinning={searching}>
                {addresses?.length ? (
                  <Radio.Group
                    value={selectedAddress?.addressId}
                    onChange={(e) => onSelectAddress(e.target.value)}
                    className="pp_map_panel__radios"
                  >
                    {addresses.map((address) => (
                      <Radio key={address.addressId} value={address.addressId}>
                        <Space direction="vertical" size={0}>
                          <Text>{address.name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {address.nameFull ?? address.nameShort}
                          </Text>
                        </Space>
                        {selectedAddress?.addressId === address.addressId && (
                          <CiLocationOn className="pp_map_panel__check" size={20} />
                        )}
                      </Radio>
                    ))}
                  </Radio.Group>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Газрын зураг дээр дарж эсвэл хайлт хийж хаягаа сонгоно уу.
                      </Text>
                    }
                    style={{ margin: 0, paddingTop: 48 }}
                  />
                )}
              </Spin>
            </div>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default AddressMap;
